import { useEffect } from 'react';

export interface OpenGraphTags {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  twitterCard?: string;
}

const DEFAULT_OG = {
  title: 'PePoll - Sua opinião com rastreabilidade',
  description: 'Participe de pesquisas de opinião com total transparência e rastreabilidade.',
  image: '/assets/brand/og-image.png',
  url: 'https://app.pepoll.org',
  type: 'website',
  twitterCard: 'summary_large_image',
};

/**
 * Hook para atualizar dinamicamente as tags Open Graph (OG)
 * Útil para compartilhamento em redes sociais e WhatsApp
 * 
 * @example
 * useOpenGraph({
 *   title: 'Pesquisa: Qual é seu prato favorito?',
 *   description: 'Participe da pesquisa e ganhe 10 pontos',
 *   image: 'https://app.pepoll.org/survey-image.png'
 * })
 */
export const useOpenGraph = (tags: OpenGraphTags = {}) => {
  useEffect(() => {
    const finalTags = { ...DEFAULT_OG, ...tags };

    // Update document title
    if (finalTags.title) {
      document.title = finalTags.title;
      updateMetaTag('og:title', finalTags.title);
    }

    // Update og:description
    if (finalTags.description) {
      updateMetaTag('og:description', finalTags.description);
      updateMetaTag('description', finalTags.description);
      updateMetaTag('twitter:description', finalTags.description);
    }

    // Update og:image
    if (finalTags.image) {
      updateMetaTag('og:image', finalTags.image);
      updateMetaTag('twitter:image', finalTags.image);
      updateMetaTag('whatsapp:image', finalTags.image);
    }

    // Update og:url
    if (finalTags.url) {
      updateMetaTag('og:url', finalTags.url);
      updateMetaTag('twitter:url', finalTags.url);
    }

    // Update og:type
    if (finalTags.type) {
      updateMetaTag('og:type', finalTags.type);
    }

    // Update twitter card
    if (finalTags.twitterCard) {
      updateMetaTag('twitter:card', finalTags.twitterCard);
    }

    // Update twitter title
    if (finalTags.title) {
      updateMetaTag('twitter:title', finalTags.title);
    }
  }, [tags]);
};

/**
 * Função auxiliar para atualizar ou criar tags meta
 */
function updateMetaTag(property: string, content: string) {
  let element = document.querySelector(
    `meta[property="${property}"], meta[name="${property}"]`
  ) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    if (property.startsWith('og:') || property.startsWith('twitter:')) {
      element.setAttribute('property', property);
    } else {
      element.setAttribute('name', property);
    }
    document.head.appendChild(element);
  }

  element.content = content;
}

/**
 * Função para gerar URL com parâmetros UTM para rastreamento
 * Útil para rastrear compartilhamentos em diferentes canais
 */
export const generateShareUrl = (
  baseUrl: string,
  source: 'whatsapp' | 'facebook' | 'twitter' | 'linkedin' | 'telegram' | 'email' | 'generic' = 'generic',
  medium: string = 'social'
): string => {
  const url = new URL(baseUrl.startsWith('http') ? baseUrl : window.location.origin + baseUrl);
  url.searchParams.set('utm_source', source);
  url.searchParams.set('utm_medium', medium);
  url.searchParams.set('utm_campaign', 'share');
  url.searchParams.set('utm_content', new Date().toISOString().split('T')[0]);
  return url.toString();
};
